import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Eye, AlertTriangle, Upload, Paperclip, X, Download, FileText } from 'lucide-react';
import { Posting, Transaction } from '../services/apiService';
import { AccountAutocomplete } from './ui/AccountAutocomplete';
import { calculateResidual, isTransactionBalanced } from '../utils/balanceUtils';
import { getTransactionDocuments, downloadDocument, DocumentEntry } from '../services/documentService';

interface TransactionFormProps {
  accounts: string[];
  initial?: Transaction | null;
  onSubmit: (data: {
    date: string;
    payee: string;
    postings: Posting[];
    tags?: Record<string, string>;
  }, pendingFiles?: File[]) => Promise<void>;
  onCancel: () => void;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ACCEPTED_FILE_TYPES = '.pdf,.png,.jpg,.jpeg,.xml';
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

function formatLedgerPreview(
  date: string,
  payee: string,
  postings: Posting[],
  tags: Record<string, string>,
): string {
  const lines = [`${date} ${payee}`];
  for (const [k, v] of Object.entries(tags)) {
    if (v) lines.push(`    ; ${k}: ${v}`);
  }
  for (const p of postings) {
    if (p.account) {
      if (p.amount != null) {
        const pad = Math.max(1, 36 - p.account.length);
        lines.push(`    ${p.account}${' '.repeat(pad)}${p.commodity || 'BRL'} ${p.amount.toFixed(2)}`);
      } else {
        lines.push(`    ${p.account}`);
      }
    }
  }
  return lines.join('\n');
}

export const TransactionForm: React.FC<TransactionFormProps> = ({
  accounts,
  initial,
  onSubmit,
  onCancel,
}) => {
  const [date, setDate] = useState(initial?.date?.replace(/\//g, '-') || new Date().toISOString().slice(0, 10));
  const [payee, setPayee] = useState(initial?.payee || '');
  const [postings, setPostings] = useState<Posting[]>(
    initial?.postings?.length
      ? initial.postings
      : [
          { account: '', amount: 0, commodity: 'BRL' },
          { account: '', amount: 0, commodity: 'BRL' },
        ],
  );
  // Track which postings were manually edited by the user
  const [manuallyEdited, setManuallyEdited] = useState<Set<number>>(
    () => new Set(initial?.postings ? initial.postings.map((_, i) => i) : [0]),
  );
  const [tags, setTags] = useState<Record<string, string>>(initial?.tags || {});
  const [tagKey, setTagKey] = useState('');
  const [tagValue, setTagValue] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Document upload state
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [existingDocs, setExistingDocs] = useState<DocumentEntry[]>([]);
  const [dragActive, setDragActive] = useState(false);

  // Load existing documents in edit mode
  useEffect(() => {
    if (initial?.id) {
      getTransactionDocuments(initial.id)
        .then((res) => setExistingDocs(res.documents))
        .catch(() => {});
    }
  }, [initial?.id]);

  const balanced = isTransactionBalanced(postings);
  const mixedCommodities = new Set(postings.map((p) => p.commodity).filter(Boolean)).size > 1;

  const updatePosting = (index: number, field: keyof Posting, value: string | number) => {
    const updated = [...postings];
    updated[index] = { ...updated[index], [field]: value };

    if (field === 'amount') {
      const newManual = new Set(manuallyEdited);
      newManual.add(index);
      setManuallyEdited(newManual);

      // Auto-balance: find the last posting not manually edited
      const autoIndex = findAutoBalanceIndex(updated, newManual);
      if (autoIndex !== -1) {
        const residual = calculateResidual(updated, autoIndex);
        if (residual) {
          updated[autoIndex] = { ...updated[autoIndex], amount: Math.round(residual.amount * 100) / 100 };
        }
      }
    }

    if (field === 'commodity') {
      // Recalculate balance after commodity change
      const autoIndex = findAutoBalanceIndex(updated, manuallyEdited);
      if (autoIndex !== -1) {
        const residual = calculateResidual(updated, autoIndex);
        if (residual) {
          updated[autoIndex] = { ...updated[autoIndex], amount: Math.round(residual.amount * 100) / 100 };
        }
      }
    }

    setPostings(updated);
  };

  const findAutoBalanceIndex = (posts: Posting[], manual: Set<number>): number => {
    // Find last posting not manually edited
    for (let i = posts.length - 1; i >= 0; i--) {
      if (!manual.has(i)) return i;
    }
    return -1;
  };

  const addPosting = () => {
    setPostings([...postings, { account: '', amount: 0, commodity: 'BRL' }]);
    // New posting is auto-balanced (not manually edited)
  };

  const removePosting = (index: number) => {
    if (postings.length <= 2) return;
    const updated = postings.filter((_, i) => i !== index);
    // Recalculate manual indices
    const newManual = new Set<number>();
    for (const idx of Array.from(manuallyEdited)) {
      if (idx < index) newManual.add(idx);
      else if (idx > index) newManual.add(idx - 1);
    }
    setManuallyEdited(newManual);
    setPostings(updated);
  };

  const addTag = () => {
    if (tagKey.trim()) {
      setTags({ ...tags, [tagKey.trim()]: tagValue.trim() });
      setTagKey('');
      setTagValue('');
    }
  };

  const removeTag = (key: string) => {
    const updated = { ...tags };
    delete updated[key];
    setTags(updated);
  };

  const addFiles = (files: FileList | File[]) => {
    const valid: File[] = [];
    for (const f of Array.from(files)) {
      if (f.size > MAX_FILE_SIZE) {
        setError(`${f.name} excede 20MB`);
        continue;
      }
      valid.push(f);
    }
    setPendingFiles((prev) => [...prev, ...valid]);
  };

  const removeFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.length) {
      addFiles(e.dataTransfer.files);
    }
  };

  const handleDownloadDoc = async (key: string) => {
    try {
      const blob = await downloadDocument(key);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = key.split('/').pop() || 'document';
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setError('Erro ao baixar documento');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const cleanTags = Object.keys(tags).length > 0 ? tags : undefined;
      await onSubmit({
        date: date.replace(/-/g, '/'),
        payee,
        postings: postings.map((p) => ({
          ...p,
          amount: Number(p.amount),
        })),
        tags: cleanTags,
      }, pendingFiles.length > 0 ? pendingFiles : undefined);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Erro ao salvar');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
          <input
            type="text"
            value={payee}
            onChange={(e) => setPayee(e.target.value)}
            placeholder="Ex: Pagamento Fornecedor"
            className="w-full border rounded-lg px-3 py-2 text-sm"
            required
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Postings</label>
          <button
            type="button"
            onClick={addPosting}
            className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-700"
          >
            <Plus className="h-4 w-4" /> Linha
          </button>
        </div>
        <div className="space-y-2">
          {postings.map((p, i) => (
            <div key={i} className="flex items-center gap-2">
              <div className="flex-1">
                <AccountAutocomplete
                  value={p.account}
                  accounts={accounts}
                  onChange={(val) => updatePosting(i, 'account', val)}
                  placeholder="Conta"
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <input
                type="number"
                step="0.01"
                value={p.amount ?? 0}
                onChange={(e) => updatePosting(i, 'amount', parseFloat(e.target.value) || 0)}
                className="w-32 border rounded-lg px-3 py-2 text-sm text-right"
                required
              />
              <select
                value={p.commodity ?? 'BRL'}
                onChange={(e) => updatePosting(i, 'commodity', e.target.value)}
                className="w-20 border rounded-lg px-2 py-2 text-sm"
              >
                <option value="BRL">BRL</option>
                <option value="USD">USD</option>
              </select>
              <button
                type="button"
                onClick={() => removePosting(i)}
                disabled={postings.length <= 2}
                className="p-2 text-gray-400 hover:text-red-500 disabled:opacity-30"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>

        {!balanced && !mixedCommodities && (
          <div className="flex items-center gap-2 mt-2 text-amber-600 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>Transação desbalanceada (soma dos postings ≠ 0)</span>
          </div>
        )}
        {mixedCommodities && (
          <div className="flex items-center gap-2 mt-2 text-gray-500 text-sm">
            <AlertTriangle className="h-4 w-4" />
            <span>Commodities diferentes — balanceamento automático não disponível</span>
          </div>
        )}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Tags (opcional)</label>
        <div className="flex flex-wrap gap-2 mb-2">
          {Object.entries(tags).map(([k, v]) => (
            <span
              key={k}
              className="inline-flex items-center gap-1 bg-gray-100 text-sm rounded-full px-3 py-1"
            >
              <span className="font-medium">{k}:</span> {v}
              <button
                type="button"
                onClick={() => removeTag(k)}
                className="text-gray-400 hover:text-red-500"
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            type="text"
            value={tagKey}
            onChange={(e) => setTagKey(e.target.value)}
            placeholder="Chave"
            className="border rounded-lg px-3 py-1.5 text-sm w-32"
          />
          <input
            type="text"
            value={tagValue}
            onChange={(e) => setTagValue(e.target.value)}
            placeholder="Valor"
            className="border rounded-lg px-3 py-1.5 text-sm flex-1"
          />
          <button
            type="button"
            onClick={addTag}
            className="px-3 py-1.5 bg-gray-100 rounded-lg text-sm hover:bg-gray-200"
          >
            Adicionar
          </button>
        </div>
      </div>

      {/* Document upload area */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          <Paperclip className="h-4 w-4 inline mr-1" />
          Documentos (opcional)
        </label>

        {/* Existing docs (edit mode) */}
        {existingDocs.length > 0 && (
          <div className="mb-2 space-y-1">
            {existingDocs.map((doc) => (
              <div key={doc.key} className="flex items-center gap-2 text-sm bg-blue-50 rounded px-3 py-1.5">
                <FileText className="h-4 w-4 text-blue-500 flex-shrink-0" />
                <span className="truncate flex-1">{doc.key.split('/').pop()}</span>
                {doc.description && <span className="text-gray-500 text-xs">{doc.description}</span>}
                <button
                  type="button"
                  onClick={() => handleDownloadDoc(doc.key)}
                  className="text-blue-500 hover:text-blue-700"
                  title="Download"
                >
                  <Download className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* New files list */}
        {pendingFiles.length > 0 && (
          <div className="mb-2 space-y-1">
            {pendingFiles.map((f, i) => (
              <div key={i} className="flex items-center gap-2 text-sm bg-gray-50 rounded px-3 py-1.5">
                <FileText className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="truncate flex-1">{f.name}</span>
                <span className="text-gray-400 text-xs">{formatFileSize(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Drop zone */}
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${
            dragActive ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
          }`}
          onClick={() => document.getElementById('txn-file-input')?.click()}
        >
          <Upload className="h-5 w-5 text-gray-400 mx-auto mb-1" />
          <p className="text-xs text-gray-500">Arraste ou clique para anexar (PDF, PNG, JPG, XML)</p>
          <input
            id="txn-file-input"
            type="file"
            className="hidden"
            accept={ACCEPTED_FILE_TYPES}
            multiple
            onChange={(e) => {
              if (e.target.files?.length) addFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>
      </div>

      <div>
        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-2"
        >
          <Eye className="h-4 w-4" />
          {showPreview ? 'Ocultar preview' : 'Preview ledger'}
        </button>
        {showPreview && (
          <pre className="bg-gray-900 text-green-400 text-sm p-4 rounded-lg overflow-x-auto font-mono">
            {formatLedgerPreview(date.replace(/-/g, '/'), payee, postings, tags)}
          </pre>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={submitting || (!balanced && !mixedCommodities)}
          className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          {submitting ? 'Salvando...' : initial ? 'Atualizar' : 'Salvar'}
        </button>
      </div>
    </form>
  );
};
